import React, { useMemo, useEffect } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB } from '../../shared/styles';
import ArcGauge from '../../components/ui/ArcGauge';

const ALL_GAME_IDS = ["memory","reaction","math","sequence","stroop","focustap","oddsymbol","directionflash","peripheralflash","numchain","wordmemory"];
const GAME_META = {
  memory:{icon:"🧠",label:"Memory",labelNL:"Geheugen",color:"#7B6FD0"},
  reaction:{icon:"⚡",label:"Reaction",labelNL:"Reactietijd",color:"#D4873A"},
  math:{icon:"➕",label:"Math",labelNL:"Rekenen",color:"#5B9ED0"},
  sequence:{icon:"🔢",label:"Sequence",labelNL:"Reeks",color:"#2A9E82"},
  stroop:{icon:"🌈",label:"Stroop",labelNL:"Stroop",color:"#C06090"},
  focustap:{icon:"🎯",label:"Focus Tap",labelNL:"Focus Tap",color:"#4AC87A"},
  oddsymbol:{icon:"🔍",label:"Odd Symbol",labelNL:"Vreemd Symbool",color:"#D4873A"},
  directionflash:{icon:"🧭",label:"Direction Flash",labelNL:"Richting Flash",color:"#C06090"},
  peripheralflash:{icon:"👁",label:"Peripheral Flash",labelNL:"Perifeer",color:"#C8A84B"},
  numchain:{icon:"🔗",label:"Number Chain",labelNL:"Nummerketen",color:"#5B9ED0"},
  wordmemory:{icon:"📝",label:"Word Memory",labelNL:"Woordgeheugen",color:"#7B6FD0"},
};

// Exercise type classification (by exId)
const MINDFULNESS_IDS = new Set(['breathing','meditation','bodyscan','focus','gratitude','boxbreathing']);
const TRAINING_IDS    = new Set(['walking','yoga','stretching','powerpose','wimhof']);

function toDateStr(d) {
  return d.getFullYear() + '-'
    + String(d.getMonth() + 1).padStart(2, '0') + '-'
    + String(d.getDate()).padStart(2, '0');
}

function computeStreak(sleepLog, moodLog, exHistory) {
  const active = new Set();
  (sleepLog  || []).forEach(e => e.date && active.add(e.date));
  (moodLog   || []).forEach(e => e.date && active.add(e.date));
  (exHistory || []).forEach(e => e.date && active.add(toDateStr(new Date(e.date))));
  const today = new Date();
  let cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (!active.has(toDateStr(cursor))) cursor.setDate(cursor.getDate() - 1);
  let streak = 0;
  while (active.has(toDateStr(cursor))) { streak++; cursor.setDate(cursor.getDate() - 1); }
  return streak;
}

function scoreColor(s) {
  if (s >= 90) return '#4AC87A';
  if (s >= 75) return '#2A9E82';
  if (s >= 60) return '#C8A84B';
  if (s >= 40) return '#D4873A';
  return '#C05050';
}

function scoreLabel(s, nl) {
  if (s >= 90) return nl ? 'Uitstekend' : 'Peak';
  if (s >= 75) return nl ? 'Sterk' : 'Strong';
  if (s >= 60) return nl ? 'Goed' : 'Good';
  if (s >= 40) return nl ? 'Matig' : 'Moderate';
  return nl ? 'Laag' : 'Low';
}

function scoreCoach(s, nl, hasSleep, hasMood) {
  if (!hasSleep && !hasMood) {
    return nl
      ? 'Log je slaap en stemming om je Sense Score te berekenen.'
      : 'Log your sleep and mood to unlock your Sense Score.';
  }
  if (!hasSleep) return nl ? 'Voeg je slaap toe voor een volledig beeld.' : 'Add your sleep for a complete picture.';
  if (!hasMood)  return nl ? 'Hoe voel je je vandaag? Log je stemming.' : 'How are you feeling? Log your mood.';
  if (s >= 90) return nl ? 'Je bent op je best vandaag. Geef alles!' : 'You\'re at your peak. Go all in today!';
  if (s >= 75) return nl ? 'Goed opgeladen. Maak er een sterke dag van.' : 'Well charged. Make it a strong day.';
  if (s >= 60) return nl ? 'Goede basis. Pas je intensiteit aan waar nodig.' : 'Good baseline. Adjust intensity where needed.';
  if (s >= 40) return nl ? 'Herstel staat centraal. Kies voor lichte beweging.' : 'Recovery first. Opt for light movement today.';
  return nl ? 'Rust en herstel zijn nu het belangrijkste.' : 'Rest and recovery matter most right now.';
}

function DailyPulse({ lang, sleepLog, moodLog, exHistory, avgSleepScore, moodScore, onNav }) {
  const nl = lang === 'nl';
  const today = toDateStr(new Date());

  const hasSleep = avgSleepScore !== null && avgSleepScore !== undefined;
  const hasMood  = moodScore !== null && moodScore !== undefined;
  const hasData  = hasSleep || hasMood;

  const streak = useMemo(() => computeStreak(sleepLog, moodLog, exHistory), [sleepLog, moodLog, exHistory]);

  const since = Date.now() - 7 * 24 * 3600 * 1000;

  const mindfulnessSessions = useMemo(() => {
    return (exHistory || []).filter(e => e.date > since && MINDFULNESS_IDS.has(e.exId)).length;
  }, [exHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const trainingSessions = useMemo(() => {
    return (exHistory || []).filter(e => e.date > since && TRAINING_IDS.has(e.exId)).length;
  }, [exHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sense Score (0-100): Sleep(35) + Mood(25) + Streak(20) + Mindfulness(10) + Training(10)
  const score = useMemo(() => {
    if (!hasData) return null;
    const sleepPts    = hasSleep ? (avgSleepScore / 10) * 35 : 0;
    const moodPts     = hasMood  ? ((moodScore - 1) / 4) * 25 : 0;
    const streakPts   = Math.min(streak / 14, 1) * 20;
    const mindfulPts  = Math.min(mindfulnessSessions / 5, 1) * 10;
    const trainingPts = Math.min(trainingSessions / 3, 1) * 10;
    const maxPossible = (hasSleep ? 35 : 0) + (hasMood ? 25 : 0) + 20 + 10 + 10;
    return Math.round(((sleepPts + moodPts + streakPts + mindfulPts + trainingPts) / maxPossible) * 100);
  }, [hasSleep, hasMood, avgSleepScore, moodScore, streak, mindfulnessSessions, trainingSessions, hasData]);

  // Aha moment: delta vs yesterday's score
  const scoreDelta = useMemo(() => {
    if (score === null) return null;
    try {
      const prev = JSON.parse(localStorage.getItem('sl_prev_sense_score') || 'null');
      if (prev === null) return null;
      return score - prev;
    } catch { return null; }
  }, [score]);

  // Persist score history + yesterday's score for delta
  useEffect(() => {
    if (score === null) return;
    try {
      const raw = JSON.parse(localStorage.getItem('sl_pulse_history') || '[]');
      const filtered = raw.filter(e => e.date !== today);
      filtered.push({ date: today, score });
      localStorage.setItem('sl_pulse_history', JSON.stringify(filtered.slice(-30)));
      // Store yesterday's score for tomorrow's delta
      const yesterday = toDateStr(new Date(Date.now() - 86400000));
      const yEntry = filtered.find(e => e.date === yesterday);
      if (yEntry) localStorage.setItem('sl_prev_sense_score', JSON.stringify(yEntry.score));
    } catch {}
  }, [score, today]);

  // 7-day trend
  const trend = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('sl_pulse_history') || '[]');
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const ds = toDateStr(d);
        const entry = raw.find(e => e.date === ds);
        days.push({ date: ds, score: entry ? entry.score : null, isToday: i === 0 });
      }
      return days;
    } catch { return []; }
  }, [score]); // eslint-disable-line react-hooks/exhaustive-deps

  const dayIdx = Math.floor(Date.now() / 86400000);
  const gameId = ALL_GAME_IDS[dayIdx % ALL_GAME_IDS.length];
  const gm = GAME_META[gameId] || { icon: '🎮', label: 'Game', labelNL: 'Spel', color: '#C8A84B' };

  const col = score !== null ? scoreColor(score) : C.silver;
  const flameColor = streak >= 7 ? '#FF6B35' : streak >= 3 ? '#D4873A' : streak > 0 ? _T.accent : '#4a5568';

  const factors = [
    {
      icon: '😴', label: nl ? 'Slaap' : 'Sleep',
      value: hasSleep ? `${avgSleepScore}/10` : '—',
      pts: hasSleep ? Math.round((avgSleepScore / 10) * 35) : null, max: 35,
      color: hasSleep ? (avgSleepScore >= 7 ? '#4AC87A' : avgSleepScore >= 5 ? '#C8A84B' : '#C05050') : C.silver,
      action: !hasSleep ? () => onNav(8) : null, hint: !hasSleep ? (nl ? 'Log slaap' : 'Log sleep') : null,
    },
    {
      icon: '💭', label: nl ? 'Stemming' : 'Mood',
      value: hasMood ? `${moodScore}/5` : '—',
      pts: hasMood ? Math.round(((moodScore - 1) / 4) * 25) : null, max: 25,
      color: hasMood ? (moodScore >= 4 ? '#4AC87A' : moodScore >= 3 ? '#C8A84B' : '#C05050') : C.silver,
      action: !hasMood ? () => onNav(8) : null, hint: !hasMood ? (nl ? 'Log stemming' : 'Log mood') : null,
    },
    {
      icon: streak >= 14 ? '🏆' : streak >= 3 ? '🔥' : streak > 0 ? '⚡' : '💤',
      label: 'Streak', value: `${streak}d`,
      pts: Math.round(Math.min(streak / 14, 1) * 20), max: 20, color: flameColor,
      action: null, hint: null,
    },
    {
      icon: '🧘', label: nl ? 'Mindful' : 'Mindful',
      value: `${mindfulnessSessions}/${nl ? 'w' : 'w'}`,
      pts: Math.round(Math.min(mindfulnessSessions / 5, 1) * 10), max: 10,
      color: mindfulnessSessions >= 4 ? '#4AC87A' : mindfulnessSessions >= 2 ? '#C8A84B' : '#D4873A',
      action: mindfulnessSessions === 0 ? () => onNav(2) : null,
      hint: mindfulnessSessions === 0 ? (nl ? 'Begin vandaag' : 'Start today') : null,
    },
    {
      icon: '🏋️', label: nl ? 'Training' : 'Training',
      value: `${trainingSessions}/${nl ? 'w' : 'w'}`,
      pts: Math.round(Math.min(trainingSessions / 3, 1) * 10), max: 10,
      color: trainingSessions >= 3 ? '#4AC87A' : trainingSessions >= 1 ? '#C8A84B' : '#D4873A',
      action: trainingSessions === 0 ? () => onNav(2) : null,
      hint: trainingSessions === 0 ? (nl ? 'Beweeg vandaag' : 'Move today') : null,
    },
  ];

  const trendMax = Math.max(...trend.map(d => d.score || 0), 1);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        background: `linear-gradient(145deg, ${_T.bg3} 0%, ${_T.bg4} 100%)`,
        border: `1px solid ${col}44`,
        borderRadius: 20,
        padding: '18px 14px 14px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Glow blob */}
        <div style={{ position:'absolute',top:-30,right:-30,width:140,height:140,borderRadius:'50%',background:`radial-gradient(circle,${col}22 0%,transparent 70%)`,pointerEvents:'none' }}/>

        {/* Header row */}
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14 }}>
          <div style={{ fontSize:10,color:col,letterSpacing:3,fontWeight:700 }}>✦ SENSE SCORE · JOUW WELZIJN-INDEX</div>
          {scoreDelta !== null && scoreDelta !== 0 && (
            <div style={{
              background: scoreDelta > 0 ? '#4AC87A20' : '#C0505020',
              border: `1px solid ${scoreDelta > 0 ? '#4AC87A' : '#C05050'}`,
              borderRadius: 20, padding: '2px 10px',
              fontSize: 11, fontWeight: 700,
              color: scoreDelta > 0 ? '#4AC87A' : '#C05050',
            }}>
              {scoreDelta > 0 ? `+${scoreDelta}` : scoreDelta} {nl ? 'vs gisteren' : 'vs yesterday'}
            </div>
          )}
        </div>

        {/* Gauge + coach */}
        <div style={{ display:'flex',alignItems:'center',gap:14,marginBottom:14 }}>
          <div style={{ flexShrink:0 }}>
            {score !== null
              ? <ArcGauge value={score} label={scoreLabel(score, nl)} color={col} size={114} />
              : (
                <div style={{ width:114,height:114,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center' }}>
                  <div style={{ fontSize:42,fontWeight:800,color:C.silver,lineHeight:1 }}>?</div>
                  <div style={{ fontSize:10,color:C.silver,marginTop:4 }}>{nl?'geen data':'no data'}</div>
                </div>
              )
            }
          </div>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:13,color:C.silver2,lineHeight:1.5,fontStyle:'italic',marginBottom:10 }}>
              {scoreCoach(score || 0, nl, hasSleep, hasMood)}
            </div>
            {!hasData && (
              <button onClick={() => onNav(8)} style={s_PB({ fontSize:12,padding:'8px 14px',borderRadius:10 })}>
                {nl ? '+ Log nu' : '+ Log now'}
              </button>
            )}
            {/* First-time aha moment */}
            {score !== null && scoreDelta === null && (
              <div style={{ fontSize:11,color:col,fontWeight:600,marginTop:4,display:'flex',alignItems:'center',gap:4 }}>
                <span style={{ fontSize:14 }}>✨</span>
                {nl ? 'Je eerste Sense Score!' : 'Your first Sense Score!'}
              </div>
            )}
          </div>
        </div>

        {/* Factor tiles — 5 columns */}
        <div style={{ display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:5,marginBottom:14 }}>
          {factors.map((f, i) => (
            <div key={i} onClick={f.action || undefined} style={{
              background:`${f.color}12`,border:`1px solid ${f.color}33`,
              borderRadius:10,padding:'7px 4px',textAlign:'center',
              cursor:f.action?'pointer':'default',
            }}>
              <div style={{ fontSize:14,marginBottom:1 }}>{f.icon}</div>
              <div style={{ fontSize:8,color:C.silver,marginBottom:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{f.label}</div>
              <div style={{ fontSize:12,fontWeight:700,color:f.color,lineHeight:1 }}>{f.value}</div>
              {f.pts !== null && (
                <div style={{ fontSize:7,color:`${f.color}99`,marginTop:1 }}>+{f.pts}/{f.max}</div>
              )}
              {f.hint && <div style={{ fontSize:7,color:f.color,marginTop:1,opacity:0.8 }}>{f.hint}</div>}
            </div>
          ))}
        </div>

        {/* 7-day trend */}
        {trend.some(d => d.score !== null) && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:9,color:C.silver,letterSpacing:2,marginBottom:6 }}>{nl?'7-DAAGSE TREND':'7-DAY TREND'}</div>
            <div style={{ display:'flex',alignItems:'flex-end',gap:3,height:36 }}>
              {trend.map((d, i) => {
                const h = d.score !== null ? Math.max((d.score / trendMax) * 36, 4) : 4;
                const c = d.score !== null ? scoreColor(d.score) : `${C.silver}30`;
                return (
                  <div key={i} style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-end',height:36 }}>
                    <div style={{ width:'100%',height:h,borderRadius:3,background:d.isToday?col:c,opacity:d.isToday?1:0.55,boxShadow:d.isToday?`0 0 6px ${col}66`:'none' }}/>
                    {d.score !== null && (
                      <div style={{ fontSize:7,color:d.isToday?col:C.silver,marginTop:2,fontWeight:d.isToday?700:400 }}>{d.score}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Daily Brain Challenge */}
        <button onClick={() => onNav(3)} style={{
          width:'100%',display:'flex',alignItems:'center',gap:10,
          background:`${gm.color}12`,border:`1px solid ${gm.color}33`,
          borderRadius:12,padding:'10px 12px',cursor:'pointer',textAlign:'left',
        }}>
          <div style={{ width:40,height:40,borderRadius:10,flexShrink:0,background:`${gm.color}20`,border:`1px solid ${gm.color}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20 }}>
            {gm.icon}
          </div>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:9,color:gm.color,letterSpacing:2,fontWeight:700 }}>🧠 {nl?'DAGELIJKSE UITDAGING':'DAILY CHALLENGE'}</div>
            <div style={{ color:C.white,fontSize:13,fontWeight:700,marginTop:1 }}>{nl?gm.labelNL:gm.label}</div>
          </div>
          <span style={{ color:gm.color,fontSize:18 }}>›</span>
        </button>
      </div>
    </div>
  );
}

export default DailyPulse;
